// src/modules/CompetitionManager/index.tsx
import React, { useState, useEffect, useCallback } from 'react';
import RolePermissionsPanel from './components/RolePermissionsPanel';
import { Permission } from '../../utils/types';
import { ROLES } from '../../utils/constants';
import apiService from '../../services/api.service';
import Toast from '../../components/Toast';

interface CompetitionManagerProps {
  seasonId: string;
  seasonName: string;
  orgId: string;
}

export interface RoleGroup {
  role: string;
  icon: React.ReactElement;
  description: string;
  color: string;
}

const ROLE_GROUPS: RoleGroup[] = [
  {
    role: 'Season Admin',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    description: 'Full administrative access to season settings',
    color: 'purple'
  },
  {
    role: 'Season Manager',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    description: 'Manage season operations and configurations',
    color: 'blue'
  },
  {
    role: 'Fixture Manager',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    description: 'Schedule and manage fixtures',
    color: 'green'
  },
  {
    role: 'Role Manager',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    description: 'Manage user roles and permissions',
    color: 'orange'
  },
  {
    role: 'Support Observer',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    description: 'View-only access for support purposes',
    color: 'gray'
  }
];

const CompetitionManager: React.FC<CompetitionManagerProps> = ({ seasonId, seasonName, orgId }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>(ROLES[0]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchPermissions(seasonId);
      setPermissions(data);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleAddPermission = (role: string) => {
    const newPermission: Permission = {
      name: '',
      email: '',
      role: role,
      status: 'to-do',
      isNew: true,
      seasonId: seasonId,
      orgId: orgId, // Use the passed orgId prop
      environment: 'production'
    };
    
    setPermissions([...permissions, newPermission]);
  };

  const handleSavePermissions = async (role: string) => {
    const rolePermissions = permissions.filter(p => p.role === role);
    
    if (rolePermissions.length === 0) {
      setToast({ message: 'No permissions to save', type: 'warning' });
      return;
    }

    // Validate all permissions have required fields
    const invalidPermissions = rolePermissions.filter(p => !p.name || !p.email);
    if (invalidPermissions.length > 0) {
      setToast({ message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await apiService.savePermissions(rolePermissions);
      
      // Update status for saved permissions
      const updated = permissions.map(p => {
        if (p.role === role) {
          return {
            ...p,
            isNew: false,
            isEditing: false,
            status: p.status === 'to-do' ? 'active' : p.status
          };
        }
        return p;
      });
      
      setPermissions(updated);
      setToast({ message: '✨ Permissions saved successfully!', type: 'success' });
      
      // Reload permissions to get IDs
      await loadPermissions();
    } catch (error) {
      console.error('Error saving permissions:', error);
      setToast({ message: 'Failed to save permissions', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getRolePermissions = (role: string) => {
    return permissions.filter(p => p.role === role);
  };

  const getRoleGroup = (role: string) => {
    return ROLE_GROUPS.find(rg => rg.role === role) || ROLE_GROUPS[0];
  };

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">
          Competition Permissions for {seasonName}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Select a role to manage its permissions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Role List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Roles ({ROLE_GROUPS.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {ROLE_GROUPS.map((roleGroup) => {
                const rolePerms = getRolePermissions(roleGroup.role);
                const isSelected = selectedRole === roleGroup.role;
                
                const colorClasses = {
                  purple: 'from-purple-50 to-purple-100 text-purple-700 border-purple-500',
                  blue: 'from-blue-50 to-blue-100 text-blue-700 border-blue-500',
                  green: 'from-green-50 to-green-100 text-green-700 border-green-500',
                  orange: 'from-orange-50 to-orange-100 text-orange-700 border-orange-500',
                  gray: 'from-gray-50 to-gray-100 text-gray-700 border-gray-500'
                }[roleGroup.color] || 'from-gray-50 to-gray-100 text-gray-700 border-gray-500';
                
                return (
                  <button
                    key={roleGroup.role}
                    onClick={() => setSelectedRole(roleGroup.role)}
                    className={`w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                      isSelected ? `bg-gradient-to-r ${colorClasses.split(' ').slice(0, 2).join(' ')} border-l-4 ${colorClasses.split(' ')[3]}` : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-r ${colorClasses.split(' ').slice(0, 2).join(' ')}`}>
                        <span className={colorClasses.split(' ')[2]}>
                          {roleGroup.icon}
                        </span>
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {roleGroup.role}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {roleGroup.description}
                        </div>
                      </div>
                    </div>
                    {rolePerms.length > 0 && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${colorClasses.split(' ').slice(0, 2).join(' ')} ${colorClasses.split(' ')[2]}`}>
                        {rolePerms.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="lg:col-span-2">
          <RolePermissionsPanel
            role={selectedRole}
            roleGroup={getRoleGroup(selectedRole)}
            permissions={getRolePermissions(selectedRole)}
            setPermissions={setPermissions}
            allPermissions={permissions}
            onAddPermission={() => handleAddPermission(selectedRole)}
            onSave={() => handleSavePermissions(selectedRole)}
            saving={saving}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default CompetitionManager;